<template>
    <div class="custom-blockui-container">
        <Fluid class="field">
            <label for="name">Name</label>
            <InputText
                id="name"
                autofocus
                v-model="newName"
                required
                :value="newName"
                style="width: 100%"
            />
        </Fluid>
        <Fluid class="field" v-if="!editRole">
            <label for="description">Description</label>
            <InputText
                id="description"
                v-model="newDescription"
                required
                style="width: 100%"
            />
        </Fluid>
        <div class="buttons">
            <Button label="Cancel" text severity="secondary" @click="cancel" />
            <Button label="Save" severity="primary" @click="closeAndSave" />
        </div>
        <div v-if="isLoading" class="moving-overlay-mask"></div>
    </div>
</template>

<script setup lang="ts">
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import InputText from "primevue/inputtext";
import { Ref, inject, onMounted, ref } from "vue";

import { useHotKeys } from "@/composables/hotkeys";
import { Role, useRolesManager, useRolesStore } from "@/stores/roles-store";

const { createRole, updateRole } = useRolesStore();
const { statusCode, isLoading } = useRolesManager();
const { addHotKey } = useHotKeys();

const editRole = ref<Role>();
const newName = ref("");
const newDescription = ref("");
const dialogRef = inject<Ref<DynamicDialogInstance>>("dialogRef");

const cancel = () => dialogRef?.value.close();
const closeAndSave = async () => {
    if (editRole.value) {
        await updateRole({
            identity: editRole.value.identity,
            name: newName.value,
        });
        if (statusCode.value < 300) dialogRef?.value.close();
    } else {
        const createdRole = await createRole(
            newName.value,
            newDescription.value
        );
        if (statusCode.value < 300) dialogRef?.value.close({ createdRole });
    }
};

onMounted(() => {
    editRole.value = dialogRef?.value.data;
    if (editRole.value) {
        newName.value = editRole.value.name;
    }
});

/**
 * Save and close on enter.
 */
addHotKey("ControlOrMeta+Enter", async () => {
    await closeAndSave();
});
</script>

<style lang="scss" scoped>
.custom-blockui-container {
    position: relative;
    display: block;
    height: 100%;
}

.moving-overlay-mask {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--p-mask-background, rgba(0, 0, 0, 0.4));
    z-index: 999;
}

.field {
    padding-bottom: 1em;

    label {
        display: block;
        margin-bottom: 0.5em;
    }
}

.buttons {
    display: flex;
    justify-content: end;
    margin-top: 1em;
    gap: 1em;
}
</style>
